// src/hooks/useInventory.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import InventoryService from '../services/inventoryService';
import toast from 'react-hot-toast';
import {
  InventoryItem,
  InventoryStats,
  GetItemsResponse,
} from '../types/inventory';

type AddItemInput = {
  batchId: string;
  tagCode: string;
  upc: string;
  count: number;
};

type UpdateItemInput = {
  batchId: string;
  tagCode: string;
  upc: string;
  count: number;
};

type UpdateCountInput = AddItemInput;

type DeleteItemInput = {
  batchId: string;
  tagCode: string;
  upc: string;
};

// Hook for inventory mutations
export const useInventory = () => {
  const queryClient = useQueryClient();

  const addItemMutation = useMutation<InventoryItem, Error, AddItemInput>({
    mutationFn: ({batchId, tagCode, upc, count }) =>
      InventoryService.addItem(batchId, tagCode, upc, count),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      toast.success('Item added successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add item');
    },
  });

  const updateItemMatchingLastTagMutation = useMutation<InventoryItem, Error, UpdateItemInput>({
      mutationFn: ({batchId, tagCode, upc, count }) =>
        InventoryService.updateItemMatchingLastTag(batchId, tagCode, upc, count),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
        toast.success('update Item Matching Last Tag success');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed toupdate Item Matching Last Tag');
      },
    });

  const updateCountMutation = useMutation<InventoryItem, Error, UpdateCountInput>({
    mutationFn: ({batchId, tagCode, upc, count }) =>
      InventoryService.updateCount(batchId, tagCode, upc, count),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      toast.success('Count updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update count');
    },
  });

  const deleteItemMutation = useMutation<{ success: boolean }, Error, DeleteItemInput>({
    mutationFn: ({batchId, tagCode, upc }) =>
      InventoryService.deleteItem(batchId, tagCode, upc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      toast.success('Item deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete item');
    },
  });

  const exportCsvMutation = useMutation<{ success: boolean }, Error, { batchId: string }>({
    mutationFn: ({ batchId }) => InventoryService.exportCsv(batchId),
    onSuccess: () => {
      toast.success('CSV exported successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to export CSV');
    },
  });

  return {
    addItem: addItemMutation.mutate,
    updateItemMatchingLastTag: updateItemMatchingLastTagMutation.mutate,
    updateCount: updateCountMutation.mutate,
    deleteItem: deleteItemMutation.mutate,
    exportCsv: exportCsvMutation.mutate,
    isLoading:
      addItemMutation.isPending ||
      updateItemMatchingLastTagMutation.isPending ||
      updateCountMutation.isPending ||
      deleteItemMutation.isPending ||
      exportCsvMutation.isPending,
  };
};

// Hook for fetching inventory items - returns the full API response
export const useInventoryItems = (
  batchId: string,
  page = 1,
  limit = 20,
  search = ''
) => {
  console.log('DEBUG - useInventoryItems called with:', { batchId, page, limit, search });
  
  return useQuery<GetItemsResponse, Error>({
    queryKey: ['inventory-items',batchId, page, limit, search],
    queryFn: async () => {
      console.log('DEBUG - queryFn executing...');
      try {
        const result = await InventoryService.getItems(batchId, page, limit, search);
        console.log('DEBUG - API result:', result);
        return result;
      } catch (error) {
        console.error('DEBUG - API error:', error);
        throw error;
      }
    },
    staleTime: 30000,
    retry: 1, // Add retry limit
    placeholderData: {
      success: true,
      data: {
        items: [],
        pagination: {
          page,
          limit,
          totalItems: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        }
      }
    }
  });
};

// Hook for fetching current stats
export const useCurrentStats = (batchId: string, tagCode: string) => {
  return useQuery<InventoryStats, Error>({
    queryKey: ['current-stats', tagCode],
    queryFn: () => InventoryService.getCurrentStats(batchId, tagCode),
    enabled: !!tagCode,
    staleTime: 10000,
  });
};

// Hook for fetching batch history
export const useBatchHistory = (page: number = 1, limit: number = 30) => {
  return useQuery<{
    data: { id: string; createdAt: string }[],
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    }
  }, Error>({
    queryKey: ['batch-history', page, limit],
    queryFn: () => InventoryService.getBatches(page, limit),
    retry: 2
  });
};